import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import Swal from 'sweetalert2';
import { FiChevronDown, FiRefreshCw } from 'react-icons/fi';
import { useUser } from '../context/UserContext';
import { hasPrivilege } from '../utils/hasPrivilege';

const EmployeeList = () => {
  const location = useLocation();
  const { employeeId } = location.state || {};
  const navigate = useNavigate();
  const { userData } = useUser();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const refreshData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await axios.get(
        process.env.REACT_APP_API_EMPLOYEE_LIST,
        {
          headers: {
            'Content-Type': 'application/json',
            'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
          }
        }
      );
      console.log(response)
      const employeeList = response.data?.data?.employees || [];

      const grouped = employeeList.map(emp => ({
        id: emp.id,
        name: emp.name,
        mobile: emp.mobileNumber,
        department: emp.department,
        role: emp.role,
        email: emp.email,
      }));

      setEmployees(grouped);

    } catch (error) {
      console.error('Failed to fetch employee list:', error);
      setError("Failed to fetch data. Please try again.");
    } finally {
      setLoading(false);
    }
  };



  useEffect(() => {
    refreshData();
  }, []);

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };
  const handleEdit = (event, id) => {
    const url = `/createEmployee?employeeId=${id}`;
    if (event?.metaKey || event?.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };


  const handleDelete = (id) => {
    Swal.fire({
      title: 'Delete this employee?',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it',
    }).then(async (res) => {
      if (res.isConfirmed) {
        try {
          const response = await axios.post(
            `${process.env.REACT_APP_API_DELETE_EMPLOYEE}?id=${id}`,
            {},
            {
              headers: {
                'Content-Type': 'application/json',
                'x-authorization': process.env.REACT_APP_AUTH_TOKEN,
              },
            }
          );

          if (response.data.success) {
            setEmployees(prev => prev.filter(e => e.id !== id));
            Swal.fire({
              icon: 'success',
              title: 'Deleted Successfully!',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
            });
          } else {
            Swal.fire({
              icon: 'error',
              title: 'Error!',
              text: response.data.success_message || 'Failed to delete.',
              toast: true,
              position: 'top-end',
              showConfirmButton: false,
              timer: 3000,
            });
          }
        } catch (error) {
          console.error('API Delete Error:', error);
          Swal.fire({
            icon: 'error',
            title: 'Error!',
            text:
              error?.response?.data?.success_message ||
              'Something went wrong. Please try again.',
            toast: true,
            position: 'top-end',
            showConfirmButton: false,
            timer: 3000,
          });
        }
      }
    });
  };


  const handleNewEmployee = (event) => {
    const url = `/createEmployee`;
    if (event.metaKey || event.ctrlKey) {
      window.open(url, "_blank");
    } else {
      navigate(url);
    }
  };
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedEmployees = [...employees].sort((a, b) => {
    if (!sortConfig.key) return 0;
    let aValue = a[sortConfig.key];
    let bValue = b[sortConfig.key];
    if (typeof aValue === 'string') {
      aValue = aValue.toLowerCase();
      bValue = bValue.toLowerCase();
    }
    return (aValue < bValue ? -1 : 1) * (sortConfig.direction === 'asc' ? 1 : -1);
  });

  const filtered = sortedEmployees.filter(e =>
    Object.values(e)
      .flatMap(v => Array.isArray(v) ? v : [v])
      .join(' ')
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const totalEntries = filtered.length;
  const totalPages = Math.ceil(totalEntries / rowsPerPage);
  const indexOfFirstRow = (currentPage - 1) * rowsPerPage;
  const currentRows = filtered.slice(indexOfFirstRow, indexOfFirstRow + rowsPerPage);
  const indexOfFirstEntry = totalEntries > 0 ? indexOfFirstRow + 1 : 0;
  const indexOfLastEntry = totalEntries > 0 ? indexOfFirstRow + currentRows.length : 0;

  const handlePageClick = (page) => setCurrentPage(page);
  const handleNextPage = () => currentPage < totalPages && setCurrentPage(prev => prev + 1);
  const handlePrevPage = () => currentPage > 1 && setCurrentPage(prev => prev - 1);
  const handleItemsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const generatePageNumbers = () => {
    const pages = [];
    const totalPagesToShow = 5;

    if (totalPages <= totalPagesToShow) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      pages.push(1);
      if (currentPage > 3) pages.push('...');
      for (let i = Math.max(2, currentPage - 1); i <= Math.min(totalPages - 1, currentPage + 1); i++) {
        pages.push(i);
      }
      if (currentPage < totalPages - 2) pages.push('...');
      pages.push(totalPages);
    }
    return pages;
  };

  const renderPagination = () => {
    return generatePageNumbers().map((page, index) =>
      typeof page === 'number' ? (
        <button
          key={index}
          className={`page-number ${currentPage === page ? 'active' : ''}`}
          onClick={() => handlePageClick(page)}
        >
          {page}
        </button>
      ) : (
        <span key={index} className="pagination-dots">
          {page}
        </span>
      )
    );
  };
  return (
    <div className="enrollment-container">
      <div className="search-bar">
        <div className="input-container">
          <input
            type="text"
            id="searchInput"
            className={`search-input ${searchTerm ? "has-value" : ""}`}
            value={searchTerm}
            onChange={handleSearchChange}
          />
          <label htmlFor="searchInput" className="floating-label">🔍 Search</label>
        </div>
        {hasPrivilege(userData, 'TICKET', 'EMPLOYEE_MODIFY') && (
          <button className="create-button-new" onClick={handleNewEmployee}>
            <span className="create-button__icon">
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="23" height="23">
                <path d="M12 5v14M5 12h14" stroke="white" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="create-button__label">Add Employee</span>
          </button>
        )}
      </div>

      <div className="items-and-dues">
        <select className="items-per-page-select" value={rowsPerPage} onChange={handleItemsPerPageChange}>
          <option value={10}>10 items per page</option>
          <option value={50}>50 items per page</option>
          <option value={75}>75 items per page</option>
        <option value={100}>100 items per page</option>
      </select>
      <div className="pagination-selector">
          <div className="button-group">
            <div className="group-label">Items per page:</div>
            <div className="button-options">
              {[10, 50, 75, 100].map((option) => (
                <button
                  key={option}
                  onClick={() =>
                    handleItemsPerPageChange({ target: { value: option } })
                  }
                  className={`page-button ${rowsPerPage === option ? 'active' : ''}`}
                >
                  <span className="button-text">{option}</span>
                </button>
              ))}
            </div>
          </div>
        </div>
        <button className="tt-action-btn" onClick={refreshData}>
          <FiRefreshCw className={`refresh-icon ${loading ? 'spin' : ''}`} />
          Refresh
        </button>
      </div>

      <div className='space' />

      <div className="tt-table-container">
        {loading ? (
          <div className="tt-loading">
            <div className="tt-loading-spinner"></div>
            <p>Loading Employees...</p>
          </div>
        ) : (
          <table className="tt-table">
            <thead>
              <tr>
                {[
                  { key: 'name', label: 'Name' },
                  { key: 'mobile', label: 'Mobile Number' },
                  { key: 'department', label: 'Department' },
                  { key: 'role', label: 'Role' },
                  { key: 'email', label: 'Email' }
                ].map(col => (
                  <th key={col.key} onClick={() => handleSort(col.key)}>
                    <div className="th-content">
                      <span>{col.label}</span>
                      <FiChevronDown
                        className={`sort-icon ${sortConfig.key === col.key ? 'active' : ''} ${sortConfig.direction === 'desc' ? 'desc' : ''}`}
                      />
                    </div>
                  </th>
                ))}
                {hasPrivilege(userData, 'TICKET', 'EMPLOYEE_MODIFY') && (
                  <th>Action</th>
                )}
              </tr>
            </thead>
            <tbody>
              {currentRows.length > 0 ? currentRows.map(e => (
                <tr key={e.id}>
                  <td>{e.name}</td>
                  <td>{e.mobile}</td>
                  <td>{e.department}</td>
                  <td>{e.role}</td>
                  <td>{e.email}</td>
                  {hasPrivilege(userData, 'TICKET', 'EMPLOYEE_MODIFY') && (
                    <td className="action-buttons ">
                      <button
                        className="edit-btn tooltip-btn"
                        onClick={(event) => handleEdit(event, e.id)}
                      >

                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 3.487a2.12 2.12 0 0 1 3.001 3.001L7.5 18.85l-4 1 1-4L16.862 3.487z" />
                        </svg>
                        <span className="tooltip-text">Edit</span>
                      </button>
                      <button className="delete-btn tooltip-btn" onClick={() => handleDelete(e.id)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" fill="none" viewBox="0 0 24 24" stroke="white" strokeWidth="2">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M6 7h12M9 7V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2M10 11v6M14 11v6M5 7h14l-1 12a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 7z" />
                        </svg>
                        <span className="tooltip-text">Delete</span>
                      </button>
                    </td>
                  )}
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} style={{ textAlign: "center" }}>No Employees Found</td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>

      <div className="pagination">
        <div className="showing-entries">
          Showing {indexOfFirstEntry} to {indexOfLastEntry} of {totalEntries} entries
        </div>
        <button
          className="prev-button"
          onClick={handlePrevPage}
          disabled={currentPage === 1}
        >
          Previous
        </button>
        {renderPagination()}
        <button
          className="next-button"
          onClick={handleNextPage}
          disabled={currentPage === totalPages}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default EmployeeList;
